import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Copy, Star, Save, Plus, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { EmailTemplate, EmailTemplateVariable } from '@/types/communication.types';
import {
  getAvailableTemplateVariables,
  getDefaultTemplateCategories,
} from '@/services/communication/emailTemplateService';

// Define the form schema with zod
const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Template name must be at least 2 characters',
  }),
  subject: z.string().min(2, {
    message: 'Subject must be at least 2 characters',
  }),
  content: z.string().min(10, {
    message: 'Content must be at least 10 characters',
  }),
  category: z.string().min(1, {
    message: 'Please select a category',
  }),
  is_default: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// EmailTemplateEditor props
interface EmailTemplateEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (values: FormValues) => Promise<void>;
  initialTemplate?: EmailTemplate;
  isLoading?: boolean;
  defaultCategories?: string[];
  customCategories?: string[];
}

export function EmailTemplateEditor({
  isOpen,
  onClose,
  onSave,
  initialTemplate,
  isLoading = false,
  defaultCategories = getDefaultTemplateCategories(),
  customCategories = [],
}: EmailTemplateEditorProps) {
  const [previewData, setPreviewData] = useState<{ subject: string; content: string }>({
    subject: '',
    content: '',
  });
  const [availableVariables] = useState<EmailTemplateVariable[]>(getAvailableTemplateVariables());
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);

  // Combine default and custom categories
  const allCategories = [
    ...defaultCategories,
    ...customCategories.filter((category) => !defaultCategories.includes(category)),
  ];

  // Set up form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialTemplate?.name || '',
      subject: initialTemplate?.subject || '',
      content: initialTemplate?.content || '',
      category: initialTemplate?.category || '',
      is_default: initialTemplate?.is_default || false,
    },
  });

  // Update form when initialTemplate changes
  useEffect(() => {
    if (initialTemplate) {
      form.reset({
        name: initialTemplate.name,
        subject: initialTemplate.subject,
        content: initialTemplate.content,
        category: initialTemplate.category,
        is_default: initialTemplate.is_default,
      });
    }
  }, [initialTemplate, form]);

  // Generate preview
  useEffect(() => {
    const subject = form.getValues('subject') || '';
    const content = form.getValues('content') || '';

    // Generate a preview with some example variables
    let previewSubject = subject;
    let previewContent = content;

    // Simple variable replacement for preview
    const previewVariables = {
      '{{contact.firstName}}': 'John',
      '{{contact.lastName}}': 'Smith',
      '{{organization.name}}': 'Acme Corporation',
      '{{user.firstName}}': 'Jane',
      '{{user.lastName}}': 'Doe',
      '{{date}}': new Date().toLocaleDateString(),
    };

    Object.entries(previewVariables).forEach(([variable, value]) => {
      previewSubject = previewSubject.replace(new RegExp(variable, 'g'), value);
      previewContent = previewContent.replace(new RegExp(variable, 'g'), value);
    });

    setPreviewData({ subject: previewSubject, content: previewContent });
  }, [form]);

  // Handle form submission
  const handleFormSubmit = async (values: FormValues) => {
    try {
      await onSave(values);
    } catch (error) {
      // Error handling omitted intentionally
    }
  };

  // Insert variable at cursor position
  const insertVariable = (variable: string) => {
    const contentField = form.getValues('content');
    const contentTextarea = document.getElementById('content') as HTMLTextAreaElement;

    if (contentTextarea) {
      const start = contentTextarea.selectionStart;
      const end = contentTextarea.selectionEnd;

      const newContent = contentField.substring(0, start) + variable + contentField.substring(end);

      form.setValue('content', newContent, { shouldDirty: true });

      // Set focus back and move cursor after the inserted variable
      setTimeout(() => {
        contentTextarea.focus();
        contentTextarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      // If selection isn't working, just append to the end
      form.setValue('content', contentField + variable, { shouldDirty: true });
    }
  };

  // Add new category
  const handleAddCategory = () => {
    if (newCategory && !allCategories.includes(newCategory)) {
      form.setValue('category', newCategory);
      setShowNewCategoryInput(false);
      setNewCategory('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialTemplate ? 'Edit Email Template' : 'Create New Email Template'}
          </DialogTitle>
          <DialogDescription>
            Design and save email templates to use in your communications.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit">Edit Template</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="mt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Template Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter template name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex items-end gap-2">
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem className="flex-grow">
                          <FormLabel>Category</FormLabel>
                          {!showNewCategoryInput ? (
                            <Select 
                              value={field.value} 
                              onValueChange={(value) => {
                                if (value === "custom") {
                                  setShowNewCategoryInput(true);
                                  // Don't change the field value when selecting custom
                                } else {
                                  field.onChange(value);
                                }
                              }}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {allCategories.map((category) => (
                                  <SelectItem key={category} value={category}>
                                    {category}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">
                                  <span className="flex items-center">
                                    <Plus className="mr-2 h-4 w-4" />
                                    Add New Category
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="flex gap-2">
                              <Input
                                placeholder="New category name"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={handleAddCategory}
                                disabled={!newCategory}
                              >
                                Add
                              </Button>
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {!showNewCategoryInput && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowNewCategoryInput(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="subject"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subject Line</FormLabel>
                      <FormControl>
                        <Input placeholder="Email subject line" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-4">
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem className="col-span-1">
                        <FormLabel>Email Content</FormLabel>
                        <FormControl>
                          <Textarea
                            id="content"
                            placeholder="Email content..."
                            className="min-h-[300px] font-mono text-sm resize-y"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>Use variables to personalize your email.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="col-span-1 space-y-4">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Available Variables</h4>
                      <div className="border rounded-md p-2 max-h-[300px] overflow-y-auto">
                        <div className="space-y-2">
                          {availableVariables.map((variable) => (
                            <div
                              key={variable.key}
                              className="flex justify-between items-center text-sm"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="flex-grow justify-start font-mono text-xs"
                                      onClick={() => insertVariable(variable.key)}
                                    >
                                      {variable.key}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="max-w-sm">
                                    <p>
                                      <strong>{variable.description}</strong>
                                    </p>
                                    {variable.example && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Example: {variable.example}
                                      </p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="is_default"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Set as Default</FormLabel>
                        <FormDescription>
                          Make this the default template for its category
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : initialTemplate ? (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Update Template
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Template
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription>
                  This is how your email will appear with variables replaced.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="border-b pb-2">
                  <div className="text-sm text-muted-foreground">Subject:</div>
                  <div className="text-lg font-medium">{previewData.subject}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Content:</div>
                  <div className="prose max-w-none p-4 border rounded-md bg-background">
                    {previewData.content.split('\n').map((line, index) => (
                      <p key={index}>{line || <br />}</p>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
